import { ESmsType, ESystemType, getSysTypeValue, removeToken } from '..';
import { Http } from '../axios-request/Axios';
import { EAxiosResponseCode, getHttpErrorMessage } from '../axios-request/IAxiosRequest';
import { getHostbaseUrl, isNull } from '@core/tools';
import { IOemBrandDTO, IOemDetail, IOemDnDTO, IOemDTO, IOemSmsDTO } from './oem-types';
import { ElMessage } from 'element-plus';

const hostUrl = getHostbaseUrl();

export async function loginToServer(param: IUserParam, bizId?: number) {
  let obj = {} as ILoginResult;

  param = JSON.parse(JSON.stringify(param)) as IUserParam;

  if (param.loginMethod === 0) {
    delete param.password;
  }

  if (param.loginMethod === 1) {
    delete param.code;
  }

  const path = param.loginMethod === 0 ? 'login/sms' : 'login';

  try {
    const { code, context, message } = await Http.post<ILoginResult>(`${hostUrl}/iam/${path}`, {
      ...param,
      loginMethod: undefined,
      bizId: __ENV_DEV__ ? undefined : bizId || undefined,
    });

    if (code === EAxiosResponseCode.Succeed) {
      obj = context || {} as ILoginResult;
      obj.systemType = isNull(obj.systemType) ? undefined : obj.systemType;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return obj;
}

/**
 * * 发送验证码
 * @param sendType
 * @param phone
 * @param isCheck 是否检查手机号存在
 */
export async function sendSmscode(sendType: ESmsType, phone: string, isCheck = false) {
  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/iam/sms/send`, {
      sendType,
      phone,
      isCheck,
    });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '发送失败，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * * 未登录发送验证码
 * @param sendType
 * @param phone
 * @param isCheck 是否检查手机号存在
 * @param bizId 配置oem的代理商Id
 */
export async function sendNotLoginSmscode(sendType: ESmsType, phone: string, bizId?: number, isCheck = false) {
  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/iam/sms/send/not-login`, {
      sendType,
      phone,
      isCheck,
      bizId,
    });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '发送失败，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

export async function findPassword(param: IGetPassWordParam) {
  param = JSON.parse(JSON.stringify(param)) as IGetPassWordParam;
  delete param.confirmPassword;

  try {
    const { code, message } = await Http.put<void>(`${hostUrl}/iam/login/findPwd`, {
      ...param,
      password: undefined,
      newPassword: param.password,
    });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

export async function doLogout() {
  let errMsg = '';

  try {
    const { code, message } = await Http.delete<void>(`${hostUrl}/iam/login/logout`, {});

    if (code !== EAxiosResponseCode.Succeed) {
      errMsg = message || '退出失败，请稍后再试~';
    }
  } catch (error) {
    errMsg = getHttpErrorMessage(error);
  }

  return errMsg;
}

/**
 * 获取登录信息
 * @returns result {@link IUserInfo}
 */
export async function queryLoginInfo() {
  let result = {} as IUserInfo;

  try {
    const { code, context, message } = await Http.get<IUserInfo>(
      `${hostUrl}/iam/user/info`, {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      result = context || {} as IUserInfo;
    } else if (message) {
      throw new Error(message);
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return result;
}

/**
 * 切换身份
 */
export async function switchIdentity(identityId: number) {
  if (identityId === undefined) {
    throw new Error('身份Id为空，请核实');
  }

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/iam/user/choice?identityId=${identityId}`, {},
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message);
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

export async function checkSwitchIdentityError(identityId: number) {
  if (identityId === undefined) {
    return '身份Id为空，请核实';
  }

  try {
    const { message } = await Http.post<void>(
      `${hostUrl}/iam/user/check-identity?identityId=${identityId}`, {},
    );

    if (message) {
      return message;
    }
  } catch (error) {
    return getHttpErrorMessage(error);
  }

  return '';
}
/**
 * 检测登录手机号是否存在
 */
export async function checkLoginPhone(phone: string) {
  let exist = false;

  try {
    const { code, context, message } = await Http.get<number>(
      `${hostUrl}/iam/login/check-phone`, { phone },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message);
    } else {
      exist = context === 1;
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return exist;
}

export async function queryOemDetail(url = window.location.hostname) {
  let data = {} as IOemDetail;
  let res = {} as IOemDetail;

  try {
    const { code, context, message } = await Http.get<IOemDetail>(
      `${hostUrl}/oem/config/detail`,
      { url },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {} as IOemDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  data.oemDTO = res.oemDTO || {} as IOemDTO;
  data.oemDnDTO = res.oemDnDTO || {} as IOemDnDTO;
  data.oemSmsDTO = res.oemSmsDTO || {} as IOemSmsDTO;
  data.oemBrandDTO = res.oemBrandDTO || {} as IOemBrandDTO;
  data.oemNull = !Object.keys(res).length || !res.oemDTO || !Object.keys(res.oemDTO).length;

  return data;
}

export const getIdentityName = (item: IIdentity) => {
  let name = '';

  if (!item) {
    return name;
  }

  switch (item.systemType) {
    case ESystemType.SAAS:
      name = '总运营后台';
      break;
    case ESystemType.Agent:
      name = item.agentDTO.agentName || '';
      break;
    case ESystemType.Company:
      name = item.companyDTO.companyName || '';
      break;
    case ESystemType.Server:
      name = item.serverDTO.serverName || '';
      break;
    default:
      break;
  }

  return name;
};

/**
 * 更新初始密码
 */
export async function updateInitialPwd(params: ISetPasswordParam) {
  let loginRes = {} as ILoginResult;

  try {
    const { code, context, message } = await Http.post<ILoginResult>(`${hostUrl}/iam/login/update-initial-pwd`, params);

    if (code === EAxiosResponseCode.Succeed) {
      loginRes = context || {} as ILoginResult;
    } else {
      throw new Error(message);
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return loginRes;
}

export interface IUserInfo {
  /** 用户id */
  userId: number;
  /** 用户名 */
  name: string;
  /** 手机号 */
  phone: string;
  /** 1 启用，0 禁用 */
  enabled: 0 | 1;
  /** 端对象列表 */
  identities?: IIdentity[];
}

export interface IIdentity {
  /** 身份id */
  identityId: number;
  /** 角色名称（用于个人中心渲染） */
  roleNames?: string[];
  /** 企业id */
  companyId?: number;
  /** 代理商id */
  agentId?: number;
  /** 服务商id */
  serverId?: number;
  /** 0:不是,1:是 */
  isAdmin?: 0 | 1;
  /**
   * 0:SAAS,1:代理商,2:企业
   * * @description {@link ESystemType}
   */
  systemType?: ESystemType;
  agentDTO?: {
    /** 0:企业代理商,1:个人代理商 */
    agentType?: 0 | 1;
    /** 0:特级,1:一级,2:二级 */
    levelType?: 0 | 1 | 2;
    agentName: string;
    id: number;
  };
  companyDTO?: {
    /** 0:ENTERPRISE,1:PROPRIETOR */
    companyType?: 'PROPRIETOR' | 'ENTERPRISE';
    companyName: string;
    id: number;
    // 普通企业 0 集团户 1 集团子企业2
    groupType?: 0 | 1 | 2;
  };
  serverDTO?: {
    serverName: string;
    id: number;
    creditCode: string;
  };
}

export interface IUserParam {
  /** 手机号 */
  username: string;
  /** 验证码（loginMethod===0） */
  code?: string;
  /** 密码（loginMethod===1） */
  password?: string;
  /** 登录方式：0 验证码，1 密码 */
  loginMethod: 0 | 1;
}

export interface IGetPassWordParam {
  /** 手机号 */
  username: string;
  /** 验证码 */
  code?: string;
  /** 密码*/
  password: string;
  /** 确认密码 */
  confirmPassword?: string;
}

export interface ILoginResult {
  /** 登录token */
  token: string;
  /** 系统类型 */
  systemType: ESystemType;
  /** 登录类型 */
  isFirstLogin: ELoginType;
  /** 首次登录时需要修改默认密码，修改默认密码时需携带确认码 */
  code: string;
  /** 是否展示系统头部导航栏 */
  showNav: number;
}

export enum ELoginType {
  /** 首次登录 */
  FirstLogin = 1,
  /** 非首次登录 */
  NotFirstLogin = 0,
}

export interface ISetPasswordParam {
  /** 手机号 */
  username: string;
  /** 新密码 */
  newPassword: string;
  /** 确认码, */
  code: string;
}

/**
 * 登录后判断身份跳转端口
 */
export async function checkIdentity() {
  const ports = {
    [ESystemType.SAAS]: __SAAS_APP_PORT__,
    [ESystemType.Agent]: __AGENT_APP_PORT__,
    [ESystemType.Company]: __COMPANY_APP_PORT__,
    [ESystemType.Server]: __SERVER_APP_PORT__,
  };

  const prefixs = {
    [ESystemType.SAAS]: 'identity-saas',
    [ESystemType.Agent]: 'identity-agent',
    [ESystemType.Company]: 'identity-company',
    [ESystemType.Server]: 'identity-server',
  };

  const systemTypeStr = getSysTypeValue();
  const systemType = isNull(systemTypeStr) ? undefined : Number(systemTypeStr);

  if (systemType === undefined) {
    ElMessage.error('获取身份失败');

    removeToken();
    return;
  }

  try {
    if (__ENV_DEV__) {
      window.location.href = `${location.protocol}//${location.hostname}:${ports[systemType]}/${prefixs[systemType!]}`;
    } else {
      window.location.href = `/${prefixs[systemType!]}`;
    }
  } catch (error) {
    ElMessage.error((error as Error).message);
  }
}
